import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import { ChevronRight, ChevronDown, Package, Loader2, Maximize2, Minimize2, ZoomIn, ZoomOut, RotateCcw, Search, BarChart3 } from "lucide-react";
import { getOnSaleProducts, OnSaleProduct, FORECAST_SEARCH_IDS } from "@/services/api/forecast";
import { cn } from "@/lib/utils";
import * as echarts from 'echarts';

// 旭日图节点接口
interface SunburstNode {
  name: string;
  value: number;
  children?: SunburstNode[];
  level?: number;
  brand?: string;
  itemStyle?: {
    color?: string;
  };
  collapsed?: boolean; // 是否折叠状态
  hasChildren?: boolean; // 是否有子节点
}

// 品牌选项接口
interface BrandOption {
  code: string;
  name: string;
}

// 品牌数据源
const BRAND_OPTIONS: BrandOption[] = [
  { code: 'B2214', name: 'KIMTRUE且初' },
  { code: 'B00206', name: 'EVERBAB艾蓓拉' },
  { code: 'B02330', name: 'ddg' },
  { code: 'B02333', name: '3odice三蒂丝' },
  { code: 'B02335', name: 'ONCUR安修泽' },
  { code: 'B02407', name: 'Aucuz奥札' },
];

const OnSaleSpuProductsArea: React.FC = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [sunburstData, setSunburstData] = useState<SunburstNode[]>([]);
  const [expandedData, setExpandedData] = useState<SunburstNode[]>([]);
  const [rawProducts, setRawProducts] = useState<OnSaleProduct[]>([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);

  // 品牌选择相关状态
  const [selectedBrandCode, setSelectedBrandCode] = useState<string>('B2214'); // 默认选择B2214

  // 主SPU详情弹窗状态
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedMainSpu, setSelectedMainSpu] = useState<string>('');
  const [detailModalData, setDetailModalData] = useState<SunburstNode[]>([]);
  const detailChartRef = useRef<HTMLDivElement>(null);
  const detailChartInstance = useRef<echarts.ECharts | null>(null);

  // 生成主SPU详情的旭日图数据
  const generateDetailSunburstData = (mainSpuName: string) => {
    if (!rawProducts.length) return [];

    // 筛选指定主SPU的产品数据
    const mainSpuProducts = rawProducts.filter(product => product.主SPU名称 === mainSpuName);

    // 构建SPU和产品数据
    const spuMap = new Map<string, Map<string, number>>();

    mainSpuProducts.forEach(product => {
      const { SPU名称, 产品名称, 销售数量 } = product;

      if (!spuMap.has(SPU名称)) {
        spuMap.set(SPU名称, new Map());
      }

      const productMap = spuMap.get(SPU名称)!;
      const currentSales = productMap.get(产品名称) || 0;
      productMap.set(产品名称, currentSales + 销售数量);
    });

    // 计算总销量
    const totalSales = Array.from(spuMap.values())
      .reduce((sum, productMap) => sum + Array.from(productMap.values()).reduce((s, v) => s + v, 0), 0);

    // 定义丰富的颜色方案
    const spuColors = [
      '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444', '#EC4899',
      '#6366F1', '#14B8A6', '#F97316', '#A855F7', '#F43F5E', '#06B6D4',
      '#84CC16', '#EAB308', '#D946EF', '#0EA5E9', '#22C55E', '#F97316',
      '#1E40AF', '#14532D', '#78350F', '#4C1D95', '#7F1D1D', '#831843'
    ];

    const productColors = [
      '#60A5FA', '#34D399', '#FBBF24', '#A78BFA', '#F87171', '#F472B6',
      '#93C5FD', '#86EFAC', '#FCD34D', '#C4B5FD', '#FCA5A5', '#FBCFE8'
    ];

    // 构建旭日图数据结构 - 主SPU为中心，SPU为第一层，产品为第二层
    const spuNodes: SunburstNode[] = [];
    let spuIndex = 0;

    // 按销量排序SPU
    const sortedSpus = Array.from(spuMap.entries())
      .sort(([, a], [, b]) => {
        const aTotal = Array.from(a[1].values()).reduce((sum, val) => sum + val, 0);
        const bTotal = Array.from(b[1].values()).reduce((sum, val) => sum + val, 0);
        return bTotal - aTotal;
      });

    sortedSpus.forEach(([spuName, productMap]) => {
      const spuColor = spuColors[spuIndex % spuColors.length];

      // 构建产品节点
      const productNodes: SunburstNode[] = [];
      let productIndex = 0;

      let totalSpuSales = 0;
      productMap.forEach((sales, productName) => {
        const productColor = productColors[productIndex % productColors.length];
        totalSpuSales += sales;

        productNodes.push({
          name: productName,
          value: sales,
          level: 6,
          itemStyle: { color: productColor }
        });
        productIndex++;
      });

      // 按销量排序产品
      productNodes.sort((a, b) => (b.value || 0) - (a.value || 0));

      spuNodes.push({
        name: spuName,
        value: totalSpuSales,
        children: productNodes,
        level: 5,
        itemStyle: { color: spuColor },
        hasChildren: productNodes.length > 0,
        collapsed: false // 弹窗模式下默认展开SPU
      });
      spuIndex++;
    });

    // 创建以主SPU为根节点的数据结构
    const detailData = [{
      name: mainSpuName,
      value: totalSales,
      children: spuNodes,
      level: 4,
      itemStyle: {
        color: '#1F2937',
        borderWidth: 4,
        borderColor: '#fff',
        shadowBlur: 20,
        shadowColor: 'rgba(0, 0, 0, 0.4)'
      },
      hasChildren: spuNodes.length > 0,
      collapsed: false
    }];

    console.log('生成的主SPU详情旭日图数据:', detailData);
    console.log(`主SPU "${mainSpuName}" 包含:`, {
      SPU数量: spuNodes.length,
      产品总数量: spuNodes.reduce((sum, spu) => sum + (spu.children?.length || 0), 0),
      总销量: totalSales
    });

    return detailData;
  };

  // 展开指定主SPU节点的函数
  const expandMainSpuNode = (mainSpuName: string) => {
    if (!rawProducts.length) return;

    // 筛选指定主SPU的产品数据
    const mainSpuProducts = rawProducts.filter(product => product.主SPU名称 === mainSpuName);

    // 构建完整的子节点数据
    const spuMap = new Map<string, Map<string, number>>();

    mainSpuProducts.forEach(product => {
      const { SPU名称, 产品名称, 销售数量 } = product;

      if (!spuMap.has(SPU名称)) {
        spuMap.set(SPU名称, new Map());
      }

      const productMap = spuMap.get(SPU名称)!;
      const currentSales = productMap.get(产品名称) || 0;
      productMap.set(产品名称, currentSales + 销售数量);
    });

    // 定义颜色方案
    const spuColors = ['#1E40AF', '#14532D', '#78350F', '#4C1D95', '#7F1D1D', '#831843'];
    const productColors = ['#60A5FA', '#34D399', '#FBBF24', '#A78BFA', '#F87171', '#F472B6'];

    let spuIndex = 0;
    const spuChildren: SunburstNode[] = [];

    spuMap.forEach((productMap, spu) => {
      const spuColor = spuColors[spuIndex % spuColors.length];

      const productChildren: SunburstNode[] = [];
      let productIndex = 0;

      let totalSpuSales = 0;
      productMap.forEach((sales, product) => {
        const productColor = productColors[productIndex % productColors.length];
        totalSpuSales += sales;

        productChildren.push({
          name: product,
          value: sales,
          level: 6,
          itemStyle: { color: productColor }
        });
        productIndex++;
      });

      // 按销量排序产品
      productChildren.sort((a, b) => (b.value || 0) - (a.value || 0));

      spuChildren.push({
        name: spu,
        value: totalSpuSales,
        children: productChildren,
        level: 5,
        itemStyle: { color: spuColor }
      });
      spuIndex++;
    });

    // 按销量排序SPU
    spuChildren.sort((a, b) => (b.value || 0) - (a.value || 0));

    return spuChildren;
  };

  // 显示主SPU详情弹窗
  const showMainSpuDetailModal = (mainSpuName: string) => {
    console.log('显示主SPU详情弹窗:', mainSpuName);
    setSelectedMainSpu(mainSpuName);
    const data = generateDetailSunburstData(mainSpuName);
    setDetailModalData(data);
    setShowDetailModal(true);
  };

  // 关闭主SPU详情弹窗
  const closeMainSpuDetailModal = () => {
    console.log('关闭主SPU详情弹窗');
    setShowDetailModal(false);
    setSelectedMainSpu('');
    setDetailModalData([]);

    // 清理弹窗中的图表实例
    if (detailChartInstance.current) {
      detailChartInstance.current.dispose();
      detailChartInstance.current = null;
    }
  };

  // 创建弹窗中的旭日图
  const createDetailChart = () => {
    if (!detailChartRef.current || detailModalData.length === 0) return;

    // 如果图表实例已存在，先销毁
    if (detailChartInstance.current) {
      detailChartInstance.current.dispose();
    }

    // 创建新的图表实例
    detailChartInstance.current = echarts.init(detailChartRef.current);

    console.log('弹窗旭日图数据:', detailModalData);

    // 计算总销量，用于百分比计算
    const totalSales = detailModalData.reduce((sum, node) => sum + (node.value || 0), 0);

    const option = {
      title: {
        text: selectedMainSpu,
        left: 'center',
        top: 'center',
        textStyle: {
          fontSize: 24,
          fontWeight: 'bold',
          color: '#1F2937'
        },
        subtext: `总销量: ${totalSales.toLocaleString()}`,
        subtextStyle: {
          fontSize: 16,
          color: '#6B7280',
          marginTop: 8
        }
      },
      tooltip: {
        trigger: 'item',
        formatter: function(params: any) {
          const levelNames = ['', '', '', '', '', '主SPU', 'SPU', '产品'];
          const levelName = levelNames[params.data.level] || '未知';
          const percentage = totalSales > 0 ? ((params.value / totalSales) * 100).toFixed(1) : '0.0';

          return `<div style="padding: 15px; min-width: 280px;">
            <div style="font-weight: bold; margin-bottom: 10px; color: #1F2937; font-size: 16px;">
              ${params.name}
            </div>
            <div style="color: #6B7280; font-size: 14px; margin-bottom: 8px;">
              层级: ${levelName}
            </div>
            <div style="color: #3B82F6; font-weight: bold; font-size: 18px; margin-bottom: 8px;">
              销量: ${params.value.toLocaleString()}
            </div>
            <div style="color: #EF4444; font-weight: bold; font-size: 16px;">
              占比: ${percentage}%
            </div>
            ${params.data.children && !params.data.hasChildren ? `<div style="color: #10B981; font-size: 12px; margin-top: 8px;">
              包含 ${params.data.children.length} 个子项
            </div>` : ''}
            ${params.data.level === 6 ? `<div style="color: #F59E0B; font-size: 12px; margin-top: 8px; font-style: italic;">
              🏷️ 最细粒度产品
            </div>` : ''}
          </div>`;
        }
      },
      series: [
        {
          name: '主SPU销量分布',
          type: 'sunburst',
          data: detailModalData,
          radius: [10, '90%'], // 调整半径范围，给中心更多空间
          center: ['50%', '50%'],
          sort: null, // 不排序，保持数据原始顺序
          emphasis: {
            focus: 'ancestor' // 聚焦到祖先节点
          },
          nodeClick: false, // 禁用点击，只展示数据
          levels: [
            {
              // 第一层：主SPU（中心）
              r0: '10%',
              r: '45%',
              itemStyle: {
                borderWidth: 4,
                borderColor: '#fff',
                shadowBlur: 20,
                shadowColor: 'rgba(0, 0, 0, 0.3)'
              },
              label: {
                rotate: 'radial',
                minAngle: 20,
                fontSize: 22,
                fontWeight: 'bold',
                color: '#fff',
                textShadowColor: 'rgba(0,0,0,0.5)',
                textShadowBlur: 3,
                formatter: function(params: any) {
                  const percentage = totalSales > 0 ? ((params.value / totalSales) * 100).toFixed(1) : '0.0';
                  return `${params.name}\n${percentage}%\n总销量: ${params.value.toLocaleString()}`;
                }
              }
            },
            {
              // 第二层：SPU
              r0: '45%',
              r: '75%',
              itemStyle: {
                borderWidth: 2,
                borderColor: '#fff',
                shadowBlur: 10,
                shadowColor: 'rgba(0, 0, 0, 0.15)'
              },
              label: {
                rotate: 'tangential',
                minAngle: 5,
                fontSize: 14,
                fontWeight: '600',
                color: '#1F2937',
                formatter: function(params: any) {
                  const percentage = totalSales > 0 ? ((params.value / totalSales) * 100).toFixed(1) : '0.0';
                  return `${params.name}\n${percentage}%`;
                }
              }
            },
            {
              // 第三层：产品
              r0: '75%',
              r: '90%',
              itemStyle: {
                borderWidth: 1,
                borderColor: '#fff',
                shadowBlur: 5,
                shadowColor: 'rgba(0, 0, 0, 0.1)'
              },
              label: {
                rotate: 'tangential',
                minAngle: 3,
                fontSize: 10,
                color: '#6B7280'
              }
            }
          ]
        }
      ]
    };

    detailChartInstance.current.setOption(option);

    // 监听窗口大小变化
    const handleResize = () => {
      if (detailChartInstance.current) {
        detailChartInstance.current.resize();
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  };

  // 递归更新展开节点的函数
  const updateExpandedNodes = (nodes: SunburstNode[], mainSpuName: string): SunburstNode[] => {
    return nodes.map(node => {
      if (node.name === mainSpuName && node.level === 4) {
        // 展开这个主SPU节点
        const spuChildren = expandMainSpuNode(mainSpuName);
        return {
          ...node,
          children: spuChildren,
          collapsed: false,
          hasChildren: true
        };
      }

      if (node.children) {
        return {
          ...node,
          children: updateExpandedNodes(node.children, mainSpuName)
        };
      }

      return node;
    });
  };

  // 获取在售商品数据
  const fetchOnSaleProducts = async () => {
    setLoading(true);
    try {
      const response = await getOnSaleProducts({
        search_id: FORECAST_SEARCH_IDS.ON_SALE_SPU_PRODUCTS,
        page_size: 1000,
        品牌编码: selectedBrandCode
      });

      console.log('API响应:', response);

      if ((response.res_status_code === '200' || response.res_status_code === '0') && response.res_content) {
        // 尝试从不同位置获取数据
        let productsData: OnSaleProduct[] = [];

        if (Array.isArray(response.res_content)) {
          productsData = response.res_content;
        } else if (response.res_content.data && Array.isArray(response.res_content.data)) {
          productsData = response.res_content.data;
        } else if (response.res_content.list && Array.isArray(response.res_content.list)) {
          productsData = response.res_content.list;
        } else if (response.res_content.data_list && Array.isArray(response.res_content.data_list)) {
          productsData = response.res_content.data_list;
        }

        console.log('解析到的商品数据:', productsData);

        // 详细检查第一个产品的所有字段
        if (productsData.length > 0) {
          console.log('=== 第一个产品详细字段检查 ===');
          console.log('所有字段名:', Object.keys(productsData[0]));
          console.log('大分类字段值:', productsData[0].大分类);
          console.log('中分类字段值:', productsData[0].中分类);
          console.log('小分类字段值:', productsData[0].小分类);
          console.log('SPU名称字段值:', productsData[0].SPU名称);
          console.log('主SPU名称字段值:', productsData[0].主SPU名称);
          console.log('产品名称字段值:', productsData[0].产品名称);
          console.log('完整对象:', JSON.stringify(productsData[0], null, 2));
          console.log('=== 字段检查结束 ===');
        }

        if (productsData.length > 0) {
          // 保存原始产品数据
          setRawProducts(productsData);

          // 构建旭日图数据结构
          const sunburstData = buildSunburstData(productsData);
          setSunburstData(sunburstData);
          setExpandedData(sunburstData); // 初始化展开数据
        } else {
          console.warn('API返回成功但没有数据');
          setSunburstData([]);
          setExpandedData([]);
          setRawProducts([]);
        }
      } else {
        console.error('API返回非成功状态:', response);
        setSunburstData([]);
        setExpandedData([]);
        setRawProducts([]);
      }
    } catch (error) {
      console.error('获取在售商品数据失败:', error);
      setSunburstData([]);
      setExpandedData([]);
      setRawProducts([]);
    } finally {
      setLoading(false);
    }
  };

  // 构建旭日图数据结构
  const buildSunburstData = (products: OnSaleProduct[]): SunburstNode[] => {
    // 统计产品分类情况 - 根据实际数据结构调整
    const productsWithCategories = products.filter(p =>
      p.大分类 && p.大分类.trim() !== '' &&
      p.中分类 && p.中分类.trim() !== '' &&
      p.小分类 && p.小分类.trim() !== ''
    );
    const productsWithoutCategories = products.filter(p =>
      !p.大分类 || p.大分类.trim() === '' ||
      !p.中分类 || p.中分类.trim() === '' ||
      !p.小分类 || p.小分类.trim() === ''
    );

    console.log('=== 数据结构分析 ===');
    console.log('总产品数量:', products.length);
    console.log('有完整分类信息的产品数量:', productsWithCategories.length);
    console.log('缺失分类信息的产品数量:', productsWithoutCategories.length);

    // 检查第一个产品是否有规格字段
    if (products.length > 0) {
      console.log('第一个产品的规格字段:', products[0].规格);
      console.log('是否有规格字段:', '规格' in products[0]);
    }
    console.log('=== 数据结构分析结束 ===');

    // 定义层级颜色方案
    const levelColors = {
      brand: ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444', '#EC4899'],
      mainSpu: ['#93C5FD', '#86EFAC', '#FCD34D', '#C4B5FD', '#FCA5A5', '#FBCFE8'],
      spu: ['#DBEAFE', '#D1FAE5', '#FEF3C7', '#EDE9FE', '#FEE2E2', '#FCE7F3'],
      product: ['#EFF6FF', '#F0FDF4', '#FFFBEB', '#FAF5FF', '#FEF2F2', '#FDF4FF']
    };

    const sunburstNodes: SunburstNode[] = [];

    // 先处理有完整分类信息的产品
    if (productsWithCategories.length > 0) {
      const categoryMap = new Map<string, Map<string, Map<string, Map<string, number>>>>();

      productsWithCategories.forEach(product => {
        const { 大分类, 中分类, 小分类, 主SPU名称, SPU名称, 产品名称, 销售数量 } = product;

        // 大分类 -> 中分类 -> 小分类 -> 主SPU名称 -> SPU名称 -> 产品名称 的层级结构
        if (!categoryMap.has(大分类)) {
          categoryMap.set(大分类, new Map());
        }

        const middleCategoryMap = categoryMap.get(大分类)!;
        if (!middleCategoryMap.has(中分类)) {
          middleCategoryMap.set(中分类, new Map());
        }

        const smallCategoryMap = middleCategoryMap.get(中分类)!;
        if (!smallCategoryMap.has(小分类)) {
          smallCategoryMap.set(小分类, new Map());
        }

        const mainSpuMap = smallCategoryMap.get(小分类)!;
        if (!mainSpuMap.has(主SPU名称)) {
          mainSpuMap.set(主SPU名称, new Map());
        }

        const spuMap = mainSpuMap.get(主SPU名称)!;
        if (!spuMap.has(SPU名称)) {
          spuMap.set(SPU名称, new Map());
        }

        const productMap = spuMap.get(SPU名称)!;
        const currentSales = productMap.get(产品名称) || 0;
        productMap.set(产品名称, currentSales + 销售数量);
      });

      // 转换为旭日图数据结构（有分类信息的产品）
      let largeCategoryIndex = 0;
      categoryMap.forEach((middleCategoryMap, largeCategory) => {
        const largeCategoryColor = levelColors.brand[largeCategoryIndex % levelColors.brand.length];

        const middleCategoryChildren: SunburstNode[] = [];
        let middleCategoryIndex = 0;

        middleCategoryMap.forEach((smallCategoryMap, middleCategory) => {
          const middleCategoryColor = levelColors.mainSpu[middleCategoryIndex % levelColors.mainSpu.length];

          const smallCategoryChildren: SunburstNode[] = [];
          let smallCategoryIndex = 0;

          smallCategoryMap.forEach((mainSpuMap, smallCategory) => {
            const smallCategoryColor = levelColors.spu[smallCategoryIndex % levelColors.spu.length];

            const mainSpuChildren: SunburstNode[] = [];
            let mainSpuIndex = 0;

            mainSpuMap.forEach((spuMap, mainSpu) => {
              const mainSpuColor = levelColors.product[mainSpuIndex % levelColors.product.length];

              // 计算主SPU的总销量
              let totalMainSpuSales = 0;
              let hasSpuChildren = false;

              spuMap.forEach((productMap, spu) => {
                // 计算SPU的总销量
                let totalSpuSales = 0;
                let hasProductChildren = false;

                productMap.forEach((sales, product) => {
                  totalSpuSales += sales;
                  hasProductChildren = true;
                });

                totalMainSpuSales += totalSpuSales;
                hasSpuChildren = true;
              });

              // 主SPU节点，初始折叠
              mainSpuChildren.push({
                name: mainSpu,
                value: totalMainSpuSales,
                level: 4,
                collapsed: true,
                hasChildren: hasSpuChildren,
                itemStyle: { color: mainSpuColor }
              });
              mainSpuIndex++;
            });

            // 按销量排序主SPU子节点
            mainSpuChildren.sort((a, b) => (b.value || 0) - (a.value || 0));

            let totalSmallCategorySales = 0;
            mainSpuChildren.forEach(mainSpu => {
              totalSmallCategorySales += mainSpu.value || 0;
            });

            smallCategoryChildren.push({
              name: smallCategory,
              value: totalSmallCategorySales,
              children: mainSpuChildren,
              level: 3,
              itemStyle: { color: smallCategoryColor }
            });
            smallCategoryIndex++;
          });

          // 按销量排序小分类子节点
          smallCategoryChildren.sort((a, b) => (b.value || 0) - (a.value || 0));

          let totalMiddleCategorySales = 0;
          smallCategoryChildren.forEach(smallCategory => {
            totalMiddleCategorySales += smallCategory.value || 0;
          });

          middleCategoryChildren.push({
            name: middleCategory,
            value: totalMiddleCategorySales,
            children: smallCategoryChildren,
            level: 2,
            itemStyle: { color: middleCategoryColor }
          });
          middleCategoryIndex++;
        });

        // 按销量排序中分类子节点
        middleCategoryChildren.sort((a, b) => (b.value || 0) - (a.value || 0));

        let totalLargeCategorySales = 0;
        middleCategoryChildren.forEach(middleCategory => {
          totalLargeCategorySales += middleCategory.value || 0;
        });

        sunburstNodes.push({
          name: largeCategory,
          value: totalLargeCategorySales,
          children: middleCategoryChildren,
          level: 1,
          itemStyle: { color: largeCategoryColor }
        });
        largeCategoryIndex++;
      });
    }

    // 处理缺失分类信息的产品，按规格或主SPU分组
    if (productsWithoutCategories.length > 0) {
      // 检查是否有规格信息来决定分组方式
      const hasSpecInfo = productsWithoutCategories.some(p => p.规格 && p.规格.trim() !== '');
      console.log('产品是否有规格信息:', hasSpecInfo);

      if (hasSpecInfo) {
        // 按规格分组
        const specMap = new Map<string, Map<string, { sales: number; products: any[] }>>();

        productsWithoutCategories.forEach(product => {
          const { 规格, 主SPU名称, SPU名称, 产品名称, 销售数量 } = product;
          const specName = 规格 || '未定义规格';

          if (!specMap.has(specName)) {
            specMap.set(specName, new Map());
          }

          const mainSpuMap = specMap.get(specName)!;
          if (!mainSpuMap.has(主SPU名称)) {
            mainSpuMap.set(主SPU名称, { sales: 0, products: [] });
          }

          const mainSpuData = mainSpuMap.get(主SPU名称)!;
          mainSpuData.sales += 销售数量;
          mainSpuData.products.push(product);
        });

        const unsortedChildren: SunburstNode[] = [];
        let specIndex = 0;

        specMap.forEach((mainSpuMap, specName) => {
          const mainSpuChildren: SunburstNode[] = [];
          let mainSpuIndex = 0;

          mainSpuMap.forEach((mainSpuData, mainSpuName) => {
            const spuMap = new Map<string, number>();
            const spuProductsMap = new Map<string, any[]>();

            mainSpuData.products.forEach(product => {
              const { SPU名称, 销售数量 } = product;

              if (!spuMap.has(SPU名称)) {
                spuMap.set(SPU名称, 0);
                spuProductsMap.set(SPU名称, []);
              }

              spuMap.set(SPU名称, spuMap.get(SPU名称)! + 销售数量);
              spuProductsMap.get(SPU名称)!.push(product);
            });

            const spuChildren: SunburstNode[] = [];
            let spuIndex = 0;

            spuMap.forEach((totalSpuSales, spuName) => {
              const spuColor = levelColors.spu[spuIndex % levelColors.spu.length];

              const productChildren: SunburstNode[] = [];
              const products = spuProductsMap.get(spuName) || [];

              products.forEach((product, productIndex) => {
                const productColor = levelColors.product[productIndex % levelColors.product.length];
                productChildren.push({
                  name: product.产品名称,
                  value: product.销售数量,
                  level: 6,
                  itemStyle: { color: productColor }
                });
              });

              // 按销量排序产品
              productChildren.sort((a, b) => (b.value || 0) - (a.value || 0));

              spuChildren.push({
                name: spuName,
                value: totalSpuSales,
                children: productChildren,
                level: 5,
                itemStyle: { color: spuColor }
              });
              spuIndex++;
            });

            // 按销量排序SPU
            spuChildren.sort((a, b) => (b.value || 0) - (a.value || 0));

            mainSpuChildren.push({
              name: mainSpuName,
              value: mainSpuData.sales,
              children: spuChildren,
              level: 4,
              itemStyle: { color: levelColors.mainSpu[mainSpuIndex % levelColors.mainSpu.length] }
            });
            mainSpuIndex++;
          });

          // 按销量排序主SPU
          mainSpuChildren.sort((a, b) => (b.value || 0) - (a.value || 0));

          unsortedChildren.push({
            name: specName,
            value: Array.from(mainSpuMap.values()).reduce((sum, data) => sum + data.sales, 0),
            children: mainSpuChildren,
            level: 2,
            itemStyle: { color: levelColors.brand[specIndex % levelColors.brand.length] }
          });
          specIndex++;
        });

        // 按销量排序规格
        unsortedChildren.sort((a, b) => (b.value || 0) - (a.value || 0));

        // 创建"按规格分类"根节点
        if (unsortedChildren.length > 0) {
          const totalUnsortedSales = unsortedChildren.reduce((sum, node) => sum + (node.value || 0), 0);

          sunburstNodes.push({
            name: '按规格分类',
            value: totalUnsortedSales,
            children: unsortedChildren,
            level: 1,
            itemStyle: { color: '#3B82F6' }
          });
        }
      } else {
        // 没有规格信息，直接按主SPU分组
        const mainSpuMap = new Map<string, { sales: number; products: any[] }>();

        productsWithoutCategories.forEach(product => {
          const { 主SPU名称, SPU名称, 产品名称, 销售数量 } = product;

          if (!mainSpuMap.has(主SPU名称)) {
            mainSpuMap.set(主SPU名称, { sales: 0, products: [] });
          }

          const mainSpuData = mainSpuMap.get(主SPU名称)!;
          mainSpuData.sales += 销售数量;
          mainSpuData.products.push(product);
        });

        const unsortedChildren: SunburstNode[] = [];
        let mainSpuIndex = 0;

        mainSpuMap.forEach((mainSpuData, mainSpuName) => {
          const spuMap = new Map<string, number>();
          const spuProductsMap = new Map<string, any[]>();

          mainSpuData.products.forEach(product => {
            const { SPU名称, 销售数量 } = product;

            if (!spuMap.has(SPU名称)) {
              spuMap.set(SPU名称, 0);
              spuProductsMap.set(SPU名称, []);
            }

            spuMap.set(SPU名称, spuMap.get(SPU名称)! + 销售数量);
            spuProductsMap.get(SPU名称)!.push(product);
          });

          const spuChildren: SunburstNode[] = [];
          let spuIndex = 0;

          spuMap.forEach((totalSpuSales, spuName) => {
            const spuColor = levelColors.spu[spuIndex % levelColors.spu.length];

            const productChildren: SunburstNode[] = [];
            const products = spuProductsMap.get(spuName) || [];

            products.forEach((product, productIndex) => {
              const productColor = levelColors.product[productIndex % levelColors.product.length];
              productChildren.push({
                name: product.产品名称,
                value: product.销售数量,
                level: 6,
                itemStyle: { color: productColor }
              });
            });

            // 按销量排序产品
            productChildren.sort((a, b) => (b.value || 0) - (a.value || 0));

            spuChildren.push({
              name: spuName,
              value: totalSpuSales,
              children: productChildren,
              level: 5,
              itemStyle: { color: spuColor }
            });
            spuIndex++;
          });

          // 按销量排序SPU
          spuChildren.sort((a, b) => (b.value || 0) - (a.value || 0));

          unsortedChildren.push({
            name: mainSpuName,
            value: mainSpuData.sales,
            children: spuChildren,
            level: 1,
            itemStyle: { color: levelColors.brand[mainSpuIndex % levelColors.brand.length] }
          });
          mainSpuIndex++;
        });

        // 按销量排序
        unsortedChildren.sort((a, b) => (b.value || 0) - (a.value || 0));

        // 创建"按主SPU分类"根节点
        if (unsortedChildren.length > 0) {
          const totalUnsortedSales = unsortedChildren.reduce((sum, node) => sum + (node.value || 0), 0);

          sunburstNodes.push({
            name: '按主SPU分类',
            value: totalUnsortedSales,
            children: unsortedChildren,
            level: 1,
            itemStyle: { color: '#8B5CF6' } // 紫色表示主SPU分类
          });
        }
      }
    }

    // 按总销量排序根节点
    sunburstNodes.sort((a, b) => (b.value || 0) - (a.value || 0));

    // 调试信息：打印数据结构
    console.log('构建的旭日图数据结构:', sunburstNodes);
    sunburstNodes.forEach((rootNode, index) => {
      console.log(`根节点 ${index + 1}: ${rootNode.name}, 子节点数量: ${rootNode.children?.length || 0}`);
    });

    return sunburstNodes;
  };

  // 初始化和更新ECharts旭日图
  const updateChart = () => {
    if (!chartRef.current || expandedData.length === 0) return;

    // 如果图表实例已存在，先销毁
    if (chartInstance.current) {
      chartInstance.current.dispose();
    }

    // 创建新的图表实例
    chartInstance.current = echarts.init(chartRef.current);

    console.log('旭日图数据:', expandedData);

    // 计算总销量，用于百分比计算
    const totalSales = expandedData.reduce((sum, node) => sum + (node.value || 0), 0);

    const option = {
      tooltip: {
        trigger: 'item',
        formatter: function(params: any) {
          const levelNames = ['', '主分类', '子分类', '子分类', '主SPU', 'SPU', '产品'];
          const levelName = levelNames[params.data.level] || '未知';
          const percentage = totalSales > 0 ? ((params.value / totalSales) * 100).toFixed(1) : '0.0';

          return `<div style="padding: 10px; min-width: 220px;">
            <div style="font-weight: bold; margin-bottom: 8px; color: #1F2937;">
              ${params.name}
            </div>
            <div style="color: #6B7280; font-size: 12px; margin-bottom: 6px;">
              层级: ${levelName}
            </div>
            <div style="color: #3B82F6; font-weight: bold; font-size: 14px;">
              销量: ${params.value.toLocaleString()}
            </div>
            <div style="color: #EF4444; font-weight: bold; font-size: 14px;">
              占比: ${percentage}%
            </div>
            ${params.data.hasChildren ? `<div style="color: #10B981; font-size: 11px; margin-top: 6px;">
              👆 点击展开 ${params.data.level === 4 ? 'SPU和产品' : '子分类'}
            </div>` : ''}
            ${params.data.children && !params.data.hasChildren ? `<div style="color: #10B981; font-size: 11px; margin-top: 6px;">
              包含 ${params.data.children.length} 个子项
            </div>` : ''}
            ${params.data.level === 6 ? `<div style="color: #F59E0B; font-size: 11px; margin-top: 4px; font-style: italic;">
              🏷️ 最细粒度产品
            </div>` : ''}
          </div>`;
        }
      },
      series: [
        {
          name: '层级销量分布',
          type: 'sunburst',
          data: expandedData,
          radius: [5, '95%'], // 增大显示面积，从5%开始到95%
          center: ['50%', '50%'], // 居中显示
          sort: null, // 不排序，保持数据原始顺序
          emphasis: {
            focus: 'ancestor' // 聚焦到祖先节点
          },
          nodeClick: 'rootToNode', // 点击根节点到当前节点的路径
          levels: [
            {
              // 第一层：主分类
              r0: '5%',
              r: '25%',
              itemStyle: {
                borderWidth: 3,
                borderColor: '#fff',
                shadowBlur: 10,
                shadowColor: 'rgba(0, 0, 0, 0.15)'
              },
              label: {
                rotate: 'tangential',
                minAngle: 8, // 降低最小角度要求，显示更多标签
                fontSize: 18,
                fontWeight: 'bold',
                color: '#fff',
                textShadowColor: 'rgba(0,0,0,0.3)',
                textShadowBlur: 2,
                formatter: function(params: any) {
                  const percentage = totalSales > 0 ? ((params.value / totalSales) * 100).toFixed(1) : '0.0';
                  return `${params.name}\n${percentage}%`;
                }
              }
            },
            {
              // 第二层：子分类
              r0: '25%',
              r: '45%',
              itemStyle: {
                borderWidth: 2,
                borderColor: '#fff',
                shadowBlur: 8,
                shadowColor: 'rgba(0, 0, 0, 0.12)'
              },
              label: {
                rotate: 'tangential',
                minAngle: 6, // 降低最小角度要求
                fontSize: 14,
                fontWeight: '600',
                color: '#1F2937',
                formatter: function(params: any) {
                  const parentValue = params.treePathInfo && params.treePathInfo.length > 0
                    ? params.treePathInfo[0].value
                    : totalSales;
                  const percentage = parentValue > 0 ? ((params.value / parentValue) * 100).toFixed(1) : '0.0';
                  return `${params.name}\n${percentage}%`;
                }
              }
            },
            {
              // 第三层：子分类
              r0: '45%',
              r: '65%',
              itemStyle: {
                borderWidth: 1.5,
                borderColor: '#fff',
                shadowBlur: 6,
                shadowColor: 'rgba(0, 0, 0, 0.1)'
              },
              label: {
                rotate: 'tangential',
                minAngle: 5, // 降低最小角度要求
                fontSize: 12,
                fontWeight: '500',
                color: '#374151',
                formatter: function(params: any) {
                  const parentValue = params.treePathInfo && params.treePathInfo.length > 1
                    ? params.treePathInfo[1].value
                    : totalSales;
                  const percentage = parentValue > 0 ? ((params.value / parentValue) * 100).toFixed(1) : '0.0';
                  return `${params.name}\n${percentage}%`;
                }
              }
            },
            {
              // 第四层：主SPU
              r0: '65%',
              r: '80%',
              itemStyle: {
                borderWidth: 1,
                borderColor: '#fff',
                shadowBlur: 4,
                shadowColor: 'rgba(0, 0, 0, 0.08)',
                cursor: 'pointer' // 鼠标指针样式
              },
              label: {
                rotate: 'tangential',
                minAngle: 4, // 降低最小角度要求
                fontSize: 11,
                fontWeight: '500',
                color: '#6B7280',
                formatter: function(params: any) {
                  const parentValue = params.treePathInfo && params.treePathInfo.length > 2
                    ? params.treePathInfo[2].value
                    : totalSales;
                  const percentage = parentValue > 0 ? ((params.value / parentValue) * 100).toFixed(1) : '0.0';
                  return params.data.hasChildren
                    ? `${params.name}\n${percentage}%\n👆`
                    : `${params.name}\n${percentage}%`;
                }
              }
            },
            {
              // 第五层：SPU（动态展开）
              r0: '80%',
              r: '90%',
              itemStyle: {
                borderWidth: 0.8,
                borderColor: '#fff',
                shadowBlur: 3,
                shadowColor: 'rgba(0, 0, 0, 0.06)'
              },
              label: {
                rotate: 'tangential',
                minAngle: 3,
                fontSize: 9,
                color: '#9CA3AF'
              }
            },
            {
              // 第六层：产品（动态展开）
              r0: '90%',
              r: '95%',
              itemStyle: {
                borderWidth: 0.5,
                borderColor: '#fff',
                shadowBlur: 2,
                shadowColor: 'rgba(0, 0, 0, 0.04)'
              },
              label: {
                show: false // 最内层不显示标签，避免过于拥挤
              }
            }
          ]
        }
      ]
    };

    chartInstance.current.setOption(option);

    // 添加点击事件监听
    chartInstance.current.on('click', function(params: any) {
      console.log('点击事件:', params); // 调试信息

      // 只处理主SPU层级的点击（level = 4）
      if (params.data && params.data.level === 4 && params.data.hasChildren) {
        const mainSpuName = params.name;
        console.log('点击主SPU:', mainSpuName);

        // 显示主SPU详情弹窗，而不是在同一图表中展开
        showMainSpuDetailModal(mainSpuName);
      }
    });

    // 监听窗口大小变化
    const handleResize = () => {
      if (chartInstance.current) {
        chartInstance.current.resize();
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  };

  // 品牌选择处理函数
  const handleBrandChange = (brandCode: string) => {
    console.log('品牌选择变化:', brandCode);
    setSelectedBrandCode(brandCode);
  };

  // 切换全屏模式
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // 更新图表
  useEffect(() => {
    if (expandedData.length > 0) {
      updateChart();
    }
  }, [expandedData, isFullscreen]);

  // 弹窗显示时创建图表
  useEffect(() => {
    if (showDetailModal && detailModalData.length > 0) {
      createDetailChart();
    }
  }, [showDetailModal, detailModalData]);

  // 初始化和品牌变化时获取数据
  useEffect(() => {
    fetchOnSaleProducts();
  }, [selectedBrandCode]);

  // 清理图表实例
  useEffect(() => {
    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose();
      }
      if (detailChartInstance.current) {
        detailChartInstance.current.dispose();
      }
    };
  }, []);

  return (
    <div className={cn(
      "min-h-screen bg-gray-50",
      isFullscreen ? "fixed inset-0 z-50 bg-white" : ""
    )}>
      {/* 页面标题区域 */}
      {!isFullscreen && (
        <div className="p-6">
          <div className="flex items-center gap-6">
            {/* 页面标题 */}
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900">在售商品旭日图</h1>
              <p className="text-gray-600 mt-1">
                旭日图展示大分类→中分类→小分类→主SPU→SPU→产品的6层层级销量分布和面积占比
              </p>
            </div>

            {/* 品牌选择区域 */}
            <div className="flex-1 max-w-xs">
              <select
                value={selectedBrandCode}
                onChange={(e) => handleBrandChange(e.target.value)}
                disabled={loading}
                className="w-full h-[42px] bg-white border border-gray-300 hover:border-blue-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200 shadow-sm rounded-md px-3 py-2 text-sm"
              >
                <option value="" disabled>请选择品牌</option>
                {BRAND_OPTIONS.map((brand) => (
                  <option key={brand.code} value={brand.code}>
                    {brand.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* 旭日图展示区域 */}
      <div className={cn(
        "px-6 pb-6",
        isFullscreen ? "p-4 h-screen" : ""
      )}>
        <div className={cn(
          "bg-gradient-to-br from-blue-50 via-white to-purple-50 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 border border-blue-100",
          isFullscreen ? "h-full p-4" : "p-8"
        )}>
          {/* 头部标题和控制按钮 */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <h3 className={cn(
                "font-bold flex items-center gap-3 text-slate-800",
                isFullscreen ? "text-xl" : "text-2xl"
              )}>
                <Package className={cn("text-blue-600", isFullscreen ? "w-5 h-5" : "w-6 h-6")} />
                层级销量旭日图
              </h3>

              </div>

            <div className="flex items-center gap-3">

              {/* 全屏控制按钮 */}
              <button
                onClick={toggleFullscreen}
                className={cn(
                  "flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 border border-gray-200",
                  "text-gray-700 hover:text-gray-900"
                )}
              >
                {isFullscreen ? (
                  <>
                    <Minimize2 className="w-4 h-4" />
                    退出全屏
                  </>
                ) : (
                  <>
                    <Maximize2 className="w-4 h-4" />
                    全屏显示
                  </>
                )}
              </button>
            </div>
          </div>

          {/* 图表容器 */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-12 h-12 animate-spin text-blue-500" />
              <span className="ml-4 text-gray-600 text-lg">数据加载中...</span>
            </div>
          ) : expandedData.length === 0 ? (
            <div className="text-center py-20 text-gray-500" style={{ height: `${isFullscreen ? window.innerHeight - 120 : 500}px` }}>
              <BarChart3 className="w-16 h-16 mx-auto mb-6 text-gray-300" />
              <p className="text-lg">暂无数据</p>
              <p className="text-sm text-gray-400 mt-2">请选择品牌后查看数据</p>
            </div>
          ) : (
            <div
              ref={chartRef}
              className={cn(
                "w-full transition-all duration-300 bg-white rounded-xl shadow-inner"
              )}
              style={{ height: `${isFullscreen ? window.innerHeight - 120 : 600}px` }}
            />
          )}

          {/* 数据统计信息 */}
          {!loading && expandedData.length > 0 && (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                <div className="text-sm text-gray-600 mb-1">主分类数量</div>
                <div className="text-2xl font-bold text-blue-600">
                  {expandedData.length}
                </div>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                <div className="text-sm text-gray-600 mb-1">总销量</div>
                <div className="text-2xl font-bold text-green-600">
                  {expandedData.reduce((sum, node) => sum + (node.value || 0), 0).toLocaleString()}
                </div>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                <div className="text-sm text-gray-600 mb-1">子分类总数</div>
                <div className="text-2xl font-bold text-purple-600">
                  {expandedData.reduce((sum, node) => sum + (node.children?.length || 0), 0)}
                </div>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                <div className="text-sm text-gray-600 mb-1">已展开主SPU</div>
                <div className="text-2xl font-bold text-orange-600">
                  {(() => {
                    let expandedMainSpuCount = 0;
                    expandedData.forEach(rootNode => {
                      rootNode.children?.forEach(mainSpu => {
                        if (!mainSpu.collapsed && mainSpu.children && mainSpu.children.length > 0) {
                          expandedMainSpuCount++;
                        }
                      });
                    });
                    return expandedMainSpuCount;
                  })()}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 主SPU详情弹窗 */}
      {showDetailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* 弹窗头部 */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
              <div className="flex items-center gap-3">
                <Package className="w-6 h-6 text-blue-600" />
                <h2 className="text-xl font-bold text-gray-900">主SPU详情</h2>
                {selectedMainSpu && (
                  <span className="text-lg font-semibold text-blue-600 bg-blue-100 px-3 py-1 rounded-full">
                    {selectedMainSpu}
                  </span>
                )}
              </div>
              <button
                onClick={closeMainSpuDetailModal}
                className="text-gray-500 hover:text-gray-700 transition-colors p-2 hover:bg-gray-100 rounded-lg"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* 弹窗内容 */}
            <div className="p-6" style={{ height: 'calc(90vh - 120px)' }}>
              <div
                ref={detailChartRef}
                className="w-full h-full bg-white rounded-lg"
              />
            </div>

            {/* 弹窗底部 */}
            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  <div className="font-semibold text-gray-900">提示:</div>
                  <div>点击SPU或产品查看详细信息，点击空白区域或关闭按钮返回</div>
                </div>
                <button
                  onClick={closeMainSpuDetailModal}
                  className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  关闭
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnSaleSpuProductsArea;